import React from "react";
// Prosumer Scheduling Page

// Default
import { useState } from "react";

// Mui
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import CardHeader from "@mui/material/CardHeader";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import Divider from "@mui/material/Divider";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid2";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";

// Custom
import Layout from "../../layout";
import CustomOutlinedSelect from "../../forms/select/OutlinedCustomSelect";
import CustomInput from "../../forms/input/Input";
import CustomDataTable from "../table/CustomTable";
import ContainedButton from "../../forms/button/ContainedButton";
import CustomToast from "../toast/CustomToast";
import { ToastState } from "../../types";

// React-icons
import { MdDelete } from "react-icons/md";
import { UnifiedIcon } from "../Icon/UnifiedIcon";

// Types
interface TimeOption {
  value: string;
  label: string;
}

interface DayAheadData {
  time: TimeOption;
  energyToSell: string;
  price: string;
  totalRevenue: string;
}

interface IntraDayData {
  time: TimeOption;
  currentGeneration: string;
  marketPrice: string;
  totalRevenue: string;
}

const ProsumerScheduling: React.FC = () => {
  const COLUMNS = ["Time", "Revenue (₹)", "Action"];

  const [dayAhead, setDayAhead] = useState<DayAheadData>({
    time: { value: "", label: "" },
    energyToSell: "",
    price: "",
    totalRevenue: "",
  });

  const [dayAheadData, setDayAheadData] = useState<DayAheadData[]>([]);

  const [intraDay, setIntraDay] = useState<IntraDayData>({
    time: { value: "", label: "" },
    currentGeneration: "",
    marketPrice: "",
    totalRevenue: "",
  });

  const [intraDayData, setIntraDayData] = useState<IntraDayData[]>([]);

  const [toast, setToast] = useState<ToastState>({
    message: "",
    severity: "",
  });

  const handleDayAheadReset = () => {
    setDayAhead({
      time: { value: "", label: "" },
      energyToSell: "",
      price: "",
      totalRevenue: "",
    });
  };

  const handleIntraDayReset = () => {
    setIntraDay({
      time: { value: "", label: "" },
      currentGeneration: "",
      marketPrice: "",
      totalRevenue: "",
    });
  };

  const saveSchedule = () => {
    if (
      dayAhead.time.value &&
      dayAhead.energyToSell &&
      dayAhead.price &&
      dayAhead.totalRevenue
    ) {
      setDayAheadData([...dayAheadData, dayAhead]);
      handleDayAheadReset();
      setToast({
        severity: "success",
        message: "Day Ahead Schedule saved successfully !",
      });
    } else {
      setToast({
        severity: "error",
        message: "Please fill in all fields before saving.",
      });
    }
  };

  const updateSchedule = () => {
    if (
      intraDay.time.value &&
      intraDay.currentGeneration &&
      intraDay.marketPrice &&
      intraDay.totalRevenue
    ) {
      setIntraDayData([...intraDayData, intraDay]);
      handleIntraDayReset();
      setToast({
        severity: "success",
        message: "Intraday Schedule saved successfully !",
      });
    } else {
      setToast({
        severity: "error",
        message: "Please fill in all fields before saving.",
      });
    }
  };

  const deleteDayAhead = (idx: number) => {
    const updatedDayAheadData = dayAheadData?.filter(
      (_, index) => idx !== index
    );
    setDayAheadData(updatedDayAheadData);
    setToast({
      severity: "success",
      message: "Record deleted successfully !",
    });
  };

  const deleteIntraDay = (idx: number) => {
    const updatedIntraDayData = intraDayData?.filter(
      (_, index) => idx !== index
    );
    setIntraDayData(updatedIntraDayData);
    setToast({
      severity: "success",
      message: "Record deleted successfully !",
    });
  };

  const generateTimeIntervals = (): TimeOption[] => {
    const intervals: TimeOption[] = [];
    for (let i = 0; i <= 24; i++) {
      const startHour = Math.floor(i / 60);
      const startMinute = i % 60;
      const endHour = Math.floor((i + 1) / 60);
      const endMinute = (i + 1) % 60;

      const startTime = `${String(startHour).padStart(2, "0")}.${String(
        startMinute
      ).padStart(2, "0")}.00`;
      const endTime = `${String(endHour).padStart(2, "0")}.${String(
        endMinute
      ).padStart(2, "0")}.00`;

      intervals.push({
        value: `${startTime} - ${endTime}`,
        label: `${startTime} - ${endTime}`,
      });
    }

    return intervals;
  };

  const timeIntervals = generateTimeIntervals();

  const rowsDayAheadContent = dayAheadData?.map((item, index) => (
    <TableRow
      key={index}
      sx={{
        height: "50px",
        background: (index + 1) % 2 ? "#F9FCFE" : "transparent",
      }}
      className="custom_table_tbody"
    >
      <TableCell>
        <Typography className="custom_table_tbody_cell">
          {item.time.label}
        </Typography>
      </TableCell>
      <TableCell>
        <Typography className="custom_table_tbody_cell">
          ₹{item.totalRevenue}
        </Typography>
      </TableCell>
      <TableCell>
        <UnifiedIcon
          icon={MdDelete}
          color="#999B9D"
          fontSize="18px"
          onClick={() => deleteDayAhead(index)}
        />
      </TableCell>
    </TableRow>
  ));

  const rowsIntraDayContent = intraDayData?.map((item, index) => (
    <TableRow
      key={index}
      sx={{
        height: "50px",
        background: (index + 1) % 2 ? "#F9FCFE" : "transparent",
      }}
      className="custom_table_tbody"
    >
      <TableCell>
        <Typography className="custom_table_tbody_cell">
          {item.time.label}
        </Typography>
      </TableCell>
      <TableCell>
        <Typography className="custom_table_tbody_cell">
          ₹{item.totalRevenue}
        </Typography>
      </TableCell>
      <TableCell>
        <UnifiedIcon
          icon={MdDelete}
          color="#999B9D"
          fontSize="18px"
          onClick={() => deleteIntraDay(index)}
        />
      </TableCell>
    </TableRow>
  ));

  return (
    <Layout>
      <CustomToast
        open={toast.message}
        severity={toast.severity}
        setToast={setToast}
      />

      <Card>
        <CardHeader
          title={<Typography variant="h5">Day Ahead Scheduling</Typography>}
        />
        <Divider />

        <CardContent>
          <Grid container spacing={2.25}>
            <Grid size={{ xs: 12, md: 2.67, lg: 2.67 }}>
              <CustomOutlinedSelect
                placeholder={"Select Time"}
                options={timeIntervals}
                label={"Select Time"}
                value={dayAhead.time}
                onChange={(newValue) => {
                  setDayAhead({
                    ...dayAhead,
                    time: newValue as TimeOption,
                  });
                }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 2.67, lg: 2.67 }}>
              <CustomInput
                label={"Energy To Sell"}
                placeholder={"Energy To Sell"}
                value={dayAhead.energyToSell}
                onChange={(e) => {
                  setDayAhead({
                    ...dayAhead,
                    energyToSell: e.target.value,
                  });
                }}
              />
            </Grid>

            <Grid size={{ xs: 12, md: 2.67, lg: 2.67 }}>
              <CustomInput
                label={"Price (₹/kWh)"}
                placeholder={"Price (₹/kWh)"}
                value={dayAhead.price}
                onChange={(e) => {
                  const price = e.target.value;
                  const energyToSell = parseFloat(dayAhead.energyToSell) || 0;
                  const priceValue = parseFloat(price) || 0;
                  const totalRevenue = energyToSell * priceValue;

                  setDayAhead({
                    ...dayAhead,
                    price: price,
                    totalRevenue:
                      totalRevenue > 0 ? totalRevenue.toFixed(2) : "",
                  });
                }}
              />
            </Grid>

            <Grid size={{ xs: 12, md: 3.99, lg: 3.99 }}>
              <Stack direction={"row"} gap={"20px"} alignItems={"center"}>
                <Typography variant="h5" mt={"25px"}>
                  =
                </Typography>
                <CustomInput
                  label={"Total Revenue"}
                  disabled={true}
                  placeholder={"Total Revenue"}
                  value={dayAhead.totalRevenue}
                />
              </Stack>
            </Grid>
          </Grid>

          <Stack
            mt={"20px"}
            direction={"row"}
            justifyContent={"flex-end"}
            gap={"10px"}
          >
            <ContainedButton
              label={"Save Schedule"}
              sx={{
                width: "fit-content",
                fontWeight: 600,
                color: "#ffffff",
              }}
              onClick={saveSchedule}
            />

            <ContainedButton
              label={"Reset Schedule"}
              sx={{
                width: "fit-content",
                fontWeight: 600,
                color: "#ffffff",
              }}
              onClick={handleDayAheadReset}
            />
          </Stack>

          {dayAheadData?.length > 0 && (
            <Box sx={{ mt: "20px" }}>
              <CustomDataTable
                sx={{ maxHeight: "400px" }}
                columns={COLUMNS}
                pagination={false}
                rowsContent={rowsDayAheadContent}
              />
            </Box>
          )}
        </CardContent>
      </Card>

      <Card sx={{ mt: "20px" }}>
        <CardHeader
          title={<Typography variant="h5">Intraday Scheduling</Typography>}
        />
        <Divider />

        <CardContent>
          <Grid container spacing={2.25}>
            <Grid size={{ xs: 12, md: 2.67, lg: 2.67 }}>
              <CustomOutlinedSelect
                placeholder={"Select Time"}
                options={timeIntervals}
                label={"Select Time"}
                onChange={(newValue) => {
                  setIntraDay({
                    ...intraDay,
                    time: newValue as TimeOption,
                  });
                }}
                value={intraDay.time}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 2.67, lg: 2.67 }}>
              <CustomInput
                label={"Current Generation"}
                placeholder={"Current Generation"}
                onChange={(e) => {
                  setIntraDay({
                    ...intraDay,
                    currentGeneration: e.target.value,
                  });
                }}
                value={intraDay.currentGeneration}
              />
            </Grid>

            <Grid size={{ xs: 12, md: 2.67, lg: 2.67 }}>
              <CustomInput
                label={"Market Price Alerts"}
                placeholder={"Market Price"}
                onChange={(e) => {
                  const marketPrice = e.target.value;
                  const currentGeneration =
                    parseFloat(intraDay.currentGeneration) || 0;
                  const marketPriceValue = parseFloat(marketPrice) || 0;
                  const totalRevenue = currentGeneration * marketPriceValue;

                  setIntraDay({
                    ...intraDay,
                    marketPrice: marketPrice,
                    totalRevenue:
                      totalRevenue > 0 ? totalRevenue.toFixed(2) : "",
                  });
                }}
                value={intraDay.marketPrice}
              />
            </Grid>

            <Grid size={{ xs: 12, md: 3.99, lg: 3.99 }}>
              <Stack direction={"row"} gap={"20px"} alignItems={"center"}>
                <Typography variant="h5" mt={"25px"}>
                  =
                </Typography>
                <CustomInput
                  label={"Total Revenue"}
                  placeholder={"Total Revenue"}
                  disabled={true}
                  value={intraDay.totalRevenue}
                />
              </Stack>
            </Grid>
          </Grid>

          <Stack
            mt={"20px"}
            direction={"row"}
            justifyContent={"flex-end"}
            gap={"10px"}
          >
            <ContainedButton
              label={"Update Schedule"}
              sx={{
                width: "fit-content",
                fontWeight: 600,
                color: "#ffffff",
              }}
              onClick={updateSchedule}
            />

            <ContainedButton
              label={"Cancel Schedule"}
              sx={{
                width: "fit-content",
                fontWeight: 600,
                color: "#ffffff",
              }}
              onClick={handleIntraDayReset}
            />
          </Stack>

          {intraDayData.length > 0 && (
            <Box sx={{ mt: "30px" }}>
              <CustomDataTable
                sx={{ maxHeight: "400px" }}
                columns={COLUMNS}
                pagination={false}
                rowsContent={rowsIntraDayContent}
              />
            </Box>
          )}
        </CardContent>
      </Card>
    </Layout>
  );
};

export default ProsumerScheduling;
